import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { ensureRobotStartCallInCode, removeRobotStartCallsFromCode } from '../../../utils/monacoRegionUtils';

const CommandManagementControl = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.loadedProject);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const enabled = project?.commandManagementEnabled ?? true;
  const readOnly = project?.readOnly ?? false;

  const handleToggle = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (readOnly) return;
    if (!project) return;
    if (!checked) {
      setConfirmOpen(true);
      return;
    }

    dispatch(projectSlice.actions.setCommandManagementEnabled(true));
    const nextCode = ensureRobotStartCallInCode(project.program.code);
    if (nextCode !== null) {
      dispatch(projectSlice.actions.queueProgramReplacement({ code: nextCode, projectId: project.id, lastModified: new Date().toISOString() }));
    }
  };

  const handleCancelDisable = () => {
    setConfirmOpen(false);
  };

  const handleConfirmDisable = () => {
    if (!project) {
      setConfirmOpen(false);
      return;
    }
    dispatch(projectSlice.actions.setCommandManagementEnabled(false));
    const nextCode = removeRobotStartCallsFromCode(project.program.code);
    if (nextCode !== null) {
      dispatch(projectSlice.actions.queueProgramReplacement({ code: nextCode, projectId: project.id, lastModified: new Date().toISOString() }));
    }
    setConfirmOpen(false);
  };

  return (
    <Box sx={{ px: 1.5, py: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={handleToggle} disabled={readOnly} />}
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('commandManagementControl.label', 'Use RobotBase')}
            </Typography>
          }
          sx={{ m: 0 }}
        />
        <Tooltip title={t('commandManagementControl.description', 'Keep RobotBase/DriveBase command management active for this program.')}>
          <IconButton
            aria-label={t('commandManagementControl.infoButton.ariaLabel', 'Learn about Use RobotBase')}
            size="small"
            onClick={() => setInfoOpen(true)}
            sx={{ p: 0.25, color: 'rapidRobot.noticeText' }}
          >
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('commandManagementControl.infoDialog.title', 'Use RobotBase')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2">
              {t(
                'commandManagementControl.infoDialog.body',
                'RobotBase manages the program loop used by configured drive bases, mechanisms, motors, sensors, and controller actions. Keep this on for normal configured programs.'
              )}
            </Typography>
            <Typography variant="body2">
              {t(
                'commandManagementControl.infoDialog.enforcement',
                'When enabled, BPS keeps RobotBase().start() in the program and prevents accidental removal. Turn it off only if you want to manage the robot start call yourself.'
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained" autoFocus>
            {t('common.ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={handleCancelDisable} maxWidth="xs" fullWidth>
        <DialogTitle>{t('commandManagementControl.disableDialog.title', 'Disable Use RobotBase?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'commandManagementControl.disableDialog.body',
              'This removes RobotBase().start() and turns off automatic RobotBase enforcement. You can turn it back on later to restore the start call.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDisable}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleConfirmDisable} color="warning" variant="contained" autoFocus>
            {t('commandManagementControl.disableDialog.confirm', 'Disable')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommandManagementControl;
